import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Stack,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { ColumnDef } from '@tonic-one/react-data-grid';
import { ExportIcon, SearchOIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useUtcOffsetStore } from 'src/models//useUtcOffsetModel';
import ExportModal from 'src/pages/Log/ExportModal';
import LogTypeSelection from 'src/pages/Log/LogType';
import { ManageColumnsBtn } from 'src/pages/Log/ManageColumnsBtn';
import TimeRange from 'src/pages/Log/TimeRange';
import { Criterion, LogState, LogTypeEnum, QueryHistoryItem } from '@Types/log';
import { isInUIC } from 'src/utils/common';

import { ProvisionApi } from '@Apis';
import { useAccountStore } from '../../models/useAccountModel';
import { ServiceNameEnum } from '../../types';
import { SERVICE_NAME_ID_MAP } from '../../utils/service';
import { reportPendo } from '../../utils/setupPendo';
import logConfig, { noNeedSortFields } from './constants';
import { LogContextProvider, useLogReducerContext } from './context';
import { LogChart, LogChartToggle } from './LogChart';
import {
  ForensicDataContent,
  MergedSecurityRiskName,
  PolicyName,
  Violator,
  BlockReason,
  FileVaSummary,
} from './LogComplexField';
import { LogFacet } from './LogFacet';
import LogTable from './LogTable';
import { MisdirectedDetailDrawer } from './MisdirectedDetailDrawer';
import SearchCriteria from './SearchCriteria';
import SmartTooltip from '../Report/SmartTooltip';
import {
  getInitLogType,
  getLogTableConfig,
  getTimeRange,
  isLogType,
  LogTimeUnit,
  removeUrlState,
} from './shared';
import { Info } from '@Components/Common/Info';
import { useLogFields } from './hooks';
import { useLayoutStore } from '../../models/useLayoutModel';
import { QueryHistoryBtn } from './QueryHistoryBtn';
import VirusLinksProvider from './context/virusLink';
import { MalwareDetectedBy } from './LogComplexField/malwareDetectedBy';
import { AuditMailDetails } from './LogComplexField/auditDetails';
import { t } from 'i18next';

const ROW_HEIGHT = 44;

const Log = () => {
  const setSearchParams = useSearchParams()[1];
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { layoutHeight } = useLayoutStore();
  const { offset } = useUtcOffsetStore();
  const { groupCompanyGuid } = useAccountStore();
  const [isLogChartOpen, setIsLogChartOpen] = useState(false);
  const logTableRef = useRef(null);

  const { t } = useTranslation();
  const {
    state,
    queryLogs,
    setLogType,
    setFacets,
    setColumnConfig,
    setPageStart,
    setQueryParams,
    setLoadChart,
    setTimeRange,
    hasNextPage,
  } = useLogReducerContext();
  const {
    type: logType,
    total,
    facets,
    allFacets,
    page,
    timeRange,
    columnConfig,
    isLoading,
    isFacetLoading,
    logs,
  } = state;

  const fields = useLogFields(logType);

  const [isExportModalOpen, toggleExportModal] = useToggle(false);

  //state for inline services provisioned or not
  const [inlineFlag, setInlineFlag] = useToggle(false);

  const changeColumnConfig = useCallback(
    (logType: LogTypeEnum, columns: ColumnDef[]) => {
      const casLogTableConfig = getLogTableConfig();
      let _config: { order: string[]; hideIds: string[] };
      const columnIds = new Set(columns.map((c) => c.id as string));
      if (
        casLogTableConfig &&
        casLogTableConfig[logType] &&
        casLogTableConfig[logType]?.order.length === columns.length &&
        //prevent key renaming
        (casLogTableConfig[logType]?.order as string[]).every(
          (v) => !t(`log.fields.${v}`).includes('log.fields')
        ) &&
        // prevent stale fields from removed columns appearing in Manage Columns
        (casLogTableConfig[logType]?.order as string[]).every((v) =>
          columnIds.has(v)
        )
      ) {
        _config = casLogTableConfig[logType];
      } else {
        // need to reset the column config in localStorage
        const order: string[] = [];
        const hideIds: string[] = [];
        columns.forEach((c) => {
          order.push(c.id as string);
        });
        //hide detection and quarantine log teamsName、productServerName column by default
        if (
          logType === LogTypeEnum.DETECTION ||
          logType === LogTypeEnum.QUARANTINE
        ) {
          hideIds.push('teamsName');
        }
        _config = {
          order,
          hideIds,
        };
      }
      setColumnConfig(_config);
    },
    [t, setColumnConfig]
  );

  //TODO: cache previous columns
  const columns = useMemo(() => {
    const ret: ColumnDef[] = [];
    const typeConfig = logConfig[logType];

    typeConfig.showFields.forEach((field) => {
      const column = constructDataGridColumns(field, logType);
      if (column) {
        ret.push(column);
      }
    });
    changeColumnConfig(logType, ret);

    return ret;
  }, [logType, changeColumnConfig]);

  useEffect(() => {
    (async () => {
      const searchParams = new URL(window.location.toString()).searchParams;
      if (isInUIC()) {
        removeUrlState();
      }
      let _type = searchParams.get('type');
      if (_type && isLogType(_type)) {
        // log type from query params
        const timeRangeInParams = JSON.parse(
          searchParams.get('timeRange') || '{ "predefined": "all"}'
        );
        setQueryParams(
          _type,
          timeRangeInParams.predefined === LogTimeUnit.Custom
            ? {
                predefined: LogTimeUnit.Custom,
                start: timeRangeInParams.start,
                end: timeRangeInParams.end,
                timezoneOffset: offset,
              }
            : getTimeRange(timeRangeInParams.predefined, _type),
          JSON.parse(decodeURIComponent(searchParams.get('facets') || '[]')),
          (JSON.parse(searchParams.get('criteria') || '[]') as Criterion[]).map(
            (item) => {
              return {
                ...item,
                label: t(`log.fields.${item.field}`),
              };
            }
          )
        );
        setSearchParams('');
      } else {
        _type = getInitLogType();
        //if current org do not provision inline service, casLogType revert to detection
        const hasInlineService = await checkInlineServices();
        if (_type === LogTypeEnum.INLINETRACKING && !hasInlineService) {
          _type = LogTypeEnum.DETECTION;
        }
        setInlineFlag(hasInlineService);
        localStorage.setItem('hasInlineServices', `${hasInlineService}`);

        setLogType(_type as LogTypeEnum);
      }
    })();
  }, []);

  useEffect(() => {
    if (logType === LogTypeEnum.URLTRACKING) setIsLogChartOpen(false);
  }, [logType]);

  return (
    <Flex height={layoutHeight}>
      <Stack width="100%">
        <Flex
          px="4x"
          py="3x"
          gap="12px"
          background={
            colorMode === 'dark'
              ? colorStyle.background.primary
              : colorStyle.background.secondary
          }
        >
          <LogTypeSelection
            type={logType}
            setLogType={setLogType}
            inlineFlag={inlineFlag}
          />
          <QueryHistoryBtn />
        </Flex>
        <Stack
          background={
            colorMode === 'dark'
              ? colorStyle.background.secondary
              : colorStyle.background.primary
          }
        >
          <LogSearchCriteria />
          <Divider />
          <Flex
            justifyContent="space-between"
            px="4x"
            py="3x"
            alignItems="center"
          >
            <LogShowItems />
            <Flex alignItems="center" gap="8px">
              <TimeRange
                type={logType}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
              />
              <Divider variant="solid" orientation="vertical" height="30px" />
              <Button
                variant="primary"
                onClick={() => {
                  reportPendo('Log.Search.Click', {
                    type: JSON.stringify(state.type),
                    sort: JSON.stringify(state.sort),
                    page: JSON.stringify(state.page),
                    searches: JSON.stringify(state.criteria),
                    facets: JSON.stringify(state.facets),
                    timeRange: JSON.stringify(state.timeRange),
                  });
                  saveQueryHistory(state, groupCompanyGuid);
                  setPageStart(1);
                  // need to update search time range
                  let latestTimeRange = state.timeRange;
                  if (latestTimeRange.predefined !== LogTimeUnit.Custom) {
                    latestTimeRange = getTimeRange(
                      latestTimeRange.predefined,
                      state.type
                    );
                  }
                  setTimeRange(latestTimeRange);
                  const _state = {
                    ...state,
                    timeRange: latestTimeRange,
                    page: {
                      start: 1,
                      size: logType === LogTypeEnum.URLTRACKING ? 20 : 100,
                    },
                  };

                  queryLogs(_state);

                  const timestamp = new Date().getTime().toString();
                  setLoadChart(timestamp);

                  window.document
                    .querySelector('.logTableContainer > div')
                    ?.scrollTo({ top: 0 });
                }}
              >
                <Icon as={SearchOIcon} />
                &nbsp;{t('log.button.search')}
              </Button>
            </Flex>
          </Flex>
        </Stack>
        <Stack
          position="relative"
          background={
            colorMode === 'dark' ? '' : colorStyle.background.secondary
          }
        >
          <LogChart
            isLogChartOpen={isLogChartOpen}
            toggleLogChart={(isOpen) => setIsLogChartOpen(isOpen)}
          />
          <Flex py="3x" px="4x">
            <LogChartToggle
              logType={logType}
              isOpen={isLogChartOpen}
              onToggle={(isOpen) => setIsLogChartOpen(isOpen)}
            />
            <Flex gap="8px" ml="auto">
              <ManageColumnsBtn />
              <Divider variant="solid" orientation="vertical" />
              <Button variant="ghost" onClick={() => toggleExportModal(true)}>
                <Icon as={ExportIcon} mr="9px" />
                {t('log.button.export')}
              </Button>
            </Flex>
          </Flex>
        </Stack>
        <Flex
          borderTop="1px solid"
          borderColor={colorMode === 'dark' ? 'gray:60' : 'gray:30'}
          flex="1"
        >
          <LogFacet
            allFacets={allFacets}
            facets={facets}
            setFacets={setFacets}
            isLoading={isFacetLoading}
            facetsOrder={fields.facetsFields}
          />
          <LogTable
            ref={logTableRef}
            logType={logType}
            columns={columns}
            page={page}
            columnConfig={columnConfig}
            logs={logs}
            total={total}
            isLoading={isLoading}
            setPageStart={setPageStart}
            setColumnConfig={setColumnConfig}
            hasNextPage={hasNextPage}
          />
        </Flex>
      </Stack>
      <ExportModal
        visible={isExportModalOpen}
        onClose={() => toggleExportModal(false)}
        columns={columns}
      />
      <MisdirectedDetailDrawer />
    </Flex>
  );
};

export default function LogWithStore() {
  return (
    <LogContextProvider>
      <VirusLinksProvider>
        <Log />
      </VirusLinksProvider>
    </LogContextProvider>
  );
}

const saveQueryHistory = (state: LogState, groupCompanyGuid: string) => {
  if (groupCompanyGuid === '') {
    return;
  }
  const { type, criteria, timeRange } = state;
  //append query history localStorage
  const key = `casLogQueryHistory.${groupCompanyGuid}`;
  const queryHistory: QueryHistoryItem[] = JSON.parse(
    localStorage.getItem(key) || '[]'
  );
  const res = [
    {
      type,
      criteria,
      timeRange,
      applied: new Date().toISOString(),
    },
    ...queryHistory,
  ];
  localStorage.setItem(key, JSON.stringify(res.slice(0, 10)));
};

const LogShowItems = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('log');
  const {
    state: { total, type, logs },
  } = useLogReducerContext();

  const showItems = {
    TOTAL: type === LogTypeEnum.URLTRACKING ? logs.length : total,
    CURRENT: logs.length,
  };

  return (
    <Flex justifyContent="space-between" align="center" gap="8px">
      <Box
        color={colorStyle.color.tertiary}
        dangerouslySetInnerHTML={{
          __html: t('log.hint.showItems', showItems),
        }}
      />
      <Info
        content={t('log.autoDeleteTips', {
          days: type === LogTypeEnum.INLINETRACKING ? 60 : 180,
        })}
      />
    </Flex>
  );
};

const LogSearchCriteria = () => {
  const {
    state: { type, criteria },
    setCriteria,
  } = useLogReducerContext();

  const { searchFields } = useLogFields(type);

  return (
    <Box width="100%" px="4x" py="3x">
      <SearchCriteria
        logType={type}
        criteria={criteria}
        setCriteria={setCriteria}
        searchFields={searchFields}
      />
    </Box>
  );
};

//construct DataGrid ColumnsDef
const constructDataGridColumns: (
  field: string,
  logType: LogTypeEnum
) => ColumnDef | null = (field, logType) => {
  const cellProps = {
    height: ROW_HEIGHT,
    display: 'flex',
    alignItems: 'center',
  };
  const hasInline =
    localStorage.getItem('hasInlineServices') === 'true' ? true : false;
  const globalSettings = JSON.parse(
    localStorage.getItem('casCurOrgGlobalSetting') || '{}'
  );

  let i18nKey = `log.fields.${field}`;

  if (field === 'smtpSender' && !hasInline) {
    return null;
  }
  if (
    ['mergeRansomwareName', 'mergeFieldFoundIn', 'feedback', 'action'].indexOf(
      field
    ) >= 0
  ) {
    //for width = 200
    const isActionUrlTracking = logType === 'urlTracking' && field === 'action';
    return {
      id: field,
      width: 200,
      header: !isActionUrlTracking ? (
        <SmartTooltip>{t(i18nKey)}</SmartTooltip>
      ) : (
        <SmartTooltip>
          {t(i18nKey)}
          <span style={{ paddingLeft: '5px' }}>
            <Info
              content={
                <span>
                  {t('log.table.urlTracking.tips1')}
                  &nbsp;
                  <span style={{ fontWeight: 'bold' }}>
                    {t('log.table.urlTracking.tips2')}
                  </span>
                </span>
              }
            />
          </span>
        </SmartTooltip>
      ),
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (
    [
      'riskDescription',
      'massiveMailsDownloadRiskDescription',
      'restoreReason',
    ].indexOf(field) >= 0
  ) {
    //for width = 300
    return {
      id: field,
      width: 300,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'abnormalLoginRiskDescription') {
    //for width = 250
    return {
      id: field,
      width: 250,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'malwareDetectedBy') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>
          <MalwareDetectedBy logItem={row.value} />
        </SmartTooltip>
      ),
    };
  } else if (field === 'violator') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => {
        return <Violator logItem={row.value} />;
      },
    };
  } else if (field === 'policy') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => {
        return <PolicyName logItem={row.value} />;
      },
    };
  } else if (field === 'fileVaSummary') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <FileVaSummary logItem={row.value} />
      ),
    };
  } else if (field === 'policySeverity') {
    //for width = 100
    return {
      id: field,
      width: 100,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'eventIds') {
    //for width = 150
    return {
      id: field,
      width: 150,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'ddaaasScanType') {
    if (globalSettings.urlSandboxFeatureShow) {
      return {
        id: field,
        width: 200,
        header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
        isSortable: !(noNeedSortFields.indexOf(field) >= 0),
        accessorKey: field,
        cellProps,
        meta: { i18nKey },
        cell: ({ row }: { row: { value: any } }) => (
          <SmartTooltip>{row.value[field]}</SmartTooltip>
        ),
      };
    } else {
      return null;
    }
  } else if (field === 'mergedSecurityRiskName') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => {
        return <MergedSecurityRiskName logItem={row.value} />;
      },
    };
  } else if (field === 'mergedWritingStyleCompromisedSender') {
    if (globalSettings.writingStyleFeatureShow) {
      return {
        id: field,
        width: 200,
        header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
        isSortable: !(noNeedSortFields.indexOf(field) >= 0),
        accessorKey: field,
        cellProps,
        meta: { i18nKey },
        cell: ({ row }: { row: { value: any } }) => (
          <SmartTooltip>{row.value[field]}</SmartTooltip>
        ),
      };
    } else {
      return null;
    }
  } else if (field === 'message') {
    const isAudit = logType === LogTypeEnum.ADUIT;
    return {
      id: field,
      width: 500,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps: isAudit
        ? { display: 'flex', alignItems: 'flex-start', height: 'auto' }
        : cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) =>
        isAudit ? (
          <AuditMailDetails logItem={row.value} />
        ) : (
          <SmartTooltip>{row.value[field]}</SmartTooltip>
        ),
    };
  } else if (field === 'orgName') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: false,
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'url') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: false,
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'totalProcessingTime') {
    if (globalSettings.logProcessingTimeEnabled) {
      return {
        id: field,
        width: 200,
        header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
        isSortable: !(noNeedSortFields.indexOf(field) >= 0),
        accessorKey: field,
        cellProps,
        meta: { i18nKey },
        cell: ({ row }: { row: { value: any } }) => (
          <SmartTooltip>{row.value[field]}</SmartTooltip>
        ),
      };
    } else {
      return null;
    }
  } else if (field === 'dlpTemplate') {
    i18nKey = 'log.fields.dlpTemplateLabel';
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: false,
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'dlpMatchedLabels') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: false,
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'dlpForensicDataContent') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => {
        return <ForensicDataContent logItem={row.value} />;
      },
    };
  } else if (field === 'timestamp') {
    // Override i18nKey based on log type
    if (logType === LogTypeEnum.URLTRACKING) {
      i18nKey = 'log.fields.timeOfClick';
    } else if (logType === LogTypeEnum.ADUIT) {
      i18nKey = 'log.fields.timestamp';
    } else {
      i18nKey = 'log.fields.timestampForLog';
    }

    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(logType === 'urlTracking'),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  } else if (field === 'productServerName') {
    if (globalSettings.smexHybridFeatureShow) {
      return {
        id: field,
        width: 200,
        header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
        isSortable: !(noNeedSortFields.indexOf(field) >= 0),
        accessorKey: field,
        cellProps,
        meta: { i18nKey },
        cell: ({ row }: { row: { value: any } }) => (
          <SmartTooltip>{row.value[field]}</SmartTooltip>
        ),
      };
    } else {
      return null;
    }
  } else if (field === 'blockReason') {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(noNeedSortFields.indexOf(field) >= 0),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => {
        return <BlockReason logItem={row.value} />;
      },
    };
  } else {
    return {
      id: field,
      width: 200,
      header: <SmartTooltip>{t(i18nKey)}</SmartTooltip>,
      isSortable: !(
        logType === 'urlTracking' || noNeedSortFields.indexOf(field) >= 0
      ),
      accessorKey: field,
      cellProps,
      meta: { i18nKey },
      cell: ({ row }: { row: { value: any } }) => (
        <SmartTooltip>{row.value[field]}</SmartTooltip>
      ),
    };
  }
};

const checkInlineServices = async () => {
  //if current org do not provision inline service, casLogType revert to detection
  const serviceIds = (await ProvisionApi.getProvisionedServices('all')).data;
  return (
    serviceIds.includes(SERVICE_NAME_ID_MAP[ServiceNameEnum.GmailInline]) ||
    serviceIds.includes(SERVICE_NAME_ID_MAP[ServiceNameEnum.ExchangeInline])
  );
};
